import { v4 as uuidv4 } from 'uuid';
import {
  clearSelectedVariation,
  fetchCharacterVariation,
  clearVariationErrors,
  setSelection
} from '../slices/voiceActorSlice';

/**
 * Selection Manager Service
 * 
 * This service manages the sequencing of selection operations to ensure
 * that each operation completes fully before the next one starts.
 */
class SelectionManager {
  constructor() {
    this.isProcessing = false;
    this.queue = [];
    this.currentOperation = null;
  }

  /**
   * Process the next operation in the queue
   */
  async processNext() {
    if (this.isProcessing || this.queue.length === 0) return;

    this.isProcessing = true;
    this.currentOperation = this.queue.shift();

    try {
      console.log(`Processing ${this.currentOperation.type} operation for ID: ${this.currentOperation.id}`);
      await this.currentOperation.execute();
      console.log(`Completed ${this.currentOperation.type} operation for ID: ${this.currentOperation.id}`);

      // Notify completion
      if (this.currentOperation.onComplete) {
        this.currentOperation.onComplete();
      }
    } catch (error) {
      console.error(`Error in ${this.currentOperation.type} operation:`, error);

      // Notify error
      if (this.currentOperation.onError) {
        this.currentOperation.onError(error);
      }
    } finally {
      this.isProcessing = false;
      this.currentOperation = null;

      // Process the next operation in the queue
      this.processNext();
    }
  }

  /**
   * Queue a new operation
   * @param {Object} operation - The operation to queue
   * @returns {Promise} A promise that resolves when the operation completes
   */
  queueOperation(operation) {
    this.queue.push(operation);
    this.processNext();
    return operation.promise; // Return the promise for awaiting
  }

  /**
   * Create a new variation operation
   * @param {Function} dispatch - Redux dispatch function
   * @param {Function} formReset - Function to reset the form state
   * @returns {Promise<string>} A promise that resolves with the new variation ID
   */
  async createNewVariation(dispatch, formReset) {
    const { promise, resolve, reject } = this.createPromise();

    const operation = {
      type: 'NEW_VARIATION',
      id: 'new',
      execute: async () => {
        try {
          // Generate UUID
          const generatedUuid = uuidv4();
          console.log('Generated new UUID for variation:', generatedUuid);

          // Reset form state
          formReset();

          // Clear selected variation in Redux
          await dispatch(clearSelectedVariation());

          // Set temporary selection with generated UUID
          await dispatch(setSelection({ level: 'variation', id: generatedUuid }));

          // Wait for React to process state updates
          await new Promise(resolve => setTimeout(resolve, 1000));

          resolve(generatedUuid);
        } catch (error) {
          reject(error);
        }
      },
      promise,
      onComplete: () => console.log('New variation creation completed'),
      onError: (error) => console.error('New variation creation failed:', error)
    };

    return this.queueOperation(operation);
  }

  /**
   * Select variation operation
   * @param {Object} variation - The variation to select
   * @param {Function} dispatch - Redux dispatch function
   * @param {Function} onVariationSelect - Callback function to handle the selected variation
   * @returns {Promise<Object>} A promise that resolves with the selected variation
   */
  // async selectVariation(variation, dispatch, onVariationSelect) {
  //   const { promise, resolve, reject } = this.createPromise();

  //   const operation = {
  //     type: 'SELECT_VARIATION',
  //     id: variation.uuid,
  //     execute: async () => {
  //       try {
  //         // Clear any previous errors
  //         dispatch(clearVariationErrors());

  //         // Fetch the variation details
  //         const result = await dispatch(fetchCharacterVariation({ variationId: variation.uuid })).unwrap();

  //         // Call the handler with the fetched data
  //         onVariationSelect(result);

  //         resolve(result);
  //       } catch (error) {
  //         reject(error);
  //       }
  //     },
  //     promise,
  //     onComplete: () => console.log(`Variation selection completed for: ${variation.uuid}`),
  //     onError: (error) => console.error(`Variation selection failed for: ${variation.uuid}`, error)
  //   };

  //   return this.queueOperation(operation);
  // }
  async selectVariation(variation, dispatch, onVariationSelect) {
    const { promise, resolve, reject } = this.createPromise();

    const operation = {
      type: 'SELECT_VARIATION',
      id: variation.uuid,
      execute: async () => {
        try {
          // Clear any previous errors
          dispatch(clearVariationErrors());

          // Fetch the variation details
          const result = await dispatch(fetchCharacterVariation({ variationId: variation.uuid })).unwrap();

          // Set the selection in Redux store
          dispatch(setSelection({ level: 'variation', id: variation.uuid }));

          // Call the handler with the fetched data
          onVariationSelect(result);

          resolve(result);
        } catch (error) {
          reject(error);
        }
      },
      promise,
      onComplete: () => console.log(`Variation selection completed for: ${variation.uuid}`),
      onError: (error) => console.error(`Variation selection failed for: ${variation.uuid}`, error)
    };

    return this.queueOperation(operation);
  }
  /**
   * Helper to create a promise with external resolve/reject
   * @returns {Object} An object containing the promise and its resolve/reject functions
   */
  createPromise() {
    let resolve, reject;
    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  }
}

// Export a singleton instance of the selection manager
export const selectionManager = new SelectionManager();
